import { Box, Flex, HStack, Tooltip } from '@chakra-ui/react';
import { UserButton, useUser } from '@clerk/nextjs';
import { DailyMeetingState } from '@daily-co/daily-js';
import { Toast } from '@web-app/components/Toast';
import { UpcomingAppointment } from '@web-app/graphql/healthie/queries/get-upcoming-provider-appointments/types';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useGetUpcomingAppointments } from '@web-app/hooks/queries/healthie/useGetUpcomingAppointments';
import { useOrganizationLogo } from '@web-app/hooks/whitelabel/useOrganizationLogo';
import styles from '@web-app/styles/mobile.module.css';
import { RecordingState } from '@web-app/types/recording-state.enum';
import { sanitizeHealthieDate } from '@web-app/utils/date-time.utils';
import { getAppointmentTags } from '@web-app/utils/patient-tags.utils';
import { format } from 'date-fns';
import Image from 'next/image';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { IoIosMore } from 'react-icons/io';

import { ChartingTag } from '../ChartingTag';
import PatientTags from '../PatientTags';
import { HeaderInfoBlock } from './components';

interface HeaderProps {
  justifyContent?: 'space-between' | 'center';
  logoColor?: 'dark' | 'white';
  withoutUserButton?: boolean;
  rightPanel?: React.ReactNode;
  recordingState?: RecordingState;
  meetingState?: DailyMeetingState | null;
  isCallOn?: boolean;
  appointment?: AppointmentResponse;
  present?: number;
}

const ONE_UNIT = 15 * 60; // 15 minutes

const Header: React.FC<HeaderProps> = ({
  withoutUserButton,
  logoColor,
  rightPanel,
  justifyContent,
  recordingState,
  isCallOn = false,
  meetingState,
  appointment,
  present = 0,
}) => {
  const startTimeRef = useRef(0);
  const totalUnitsRef = useRef<HTMLDivElement>(null);
  const totalTimeRef = useRef<HTMLDivElement>(null);
  const subTextRef = useRef<HTMLDivElement>(null);
  const subText2Ref = useRef<HTMLDivElement>(null);
  const nextUnitRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();
  const logoConfig = useOrganizationLogo();

  const setUnits = useCallback(() => {
    const EIGHT_MINUTES_IN_SECONDS = 8 * 60;

    const elapsedSeconds = Math.floor(
      (Date.now() - startTimeRef.current) / 1000,
    );

    // ---- Calculate units based on 8-minute threshold rule ----
    const wholeUnits = Math.floor(elapsedSeconds / ONE_UNIT);
    const secondsIntoCurrentUnit = elapsedSeconds % ONE_UNIT;

    // Add one more unit if we're 8+ minutes into the current unit
    const billableUnits =
      wholeUnits + (secondsIntoCurrentUnit >= EIGHT_MINUTES_IN_SECONDS ? 1 : 0);

    const totalMinutes = Math.floor(elapsedSeconds / 60);

    let secondsUntilNextUnit;

    if (secondsIntoCurrentUnit >= EIGHT_MINUTES_IN_SECONDS) {
      // Already earned this unit, countdown to 8 minutes into next unit
      secondsUntilNextUnit =
        ONE_UNIT + EIGHT_MINUTES_IN_SECONDS - secondsIntoCurrentUnit;
    } else {
      // Not yet earned this unit, countdown to 8 minutes into current unit
      secondsUntilNextUnit = EIGHT_MINUTES_IN_SECONDS - secondsIntoCurrentUnit;
    }

    const nextUnitMinutes = Math.floor(secondsUntilNextUnit / 60);
    const nextUnitSeconds = secondsUntilNextUnit % 60;
    const formattedNextUnitTime = `${nextUnitMinutes}:${`0${nextUnitSeconds}`.slice(-2)}`;

    if (totalUnitsRef.current) {
      totalUnitsRef.current.innerText = billableUnits.toString();
    }

    if (totalTimeRef.current) {
      totalTimeRef.current.innerText = totalMinutes.toString();
    }

    if (nextUnitRef.current) {
      nextUnitRef.current.innerText = formattedNextUnitTime;
    }

    if (subTextRef.current) {
      subTextRef.current.innerText = `min${totalMinutes > 1 ? 's' : ''}`;
    }

    if (subText2Ref.current) {
      subText2Ref.current.innerText = `unit${billableUnits > 1 ? 's' : ''}`;
    }

    // Schedule next update
    setTimeout(setUnits, 1000);
  }, []);

  useEffect(() => {
    if (present < 2 || startTimeRef.current) return;

    startTimeRef.current = Date.now();

    setUnits();
  }, [present, setUnits]);

  const chartingNoteEnabled = !!user;
  const isProviderInCall =
    chartingNoteEnabled && meetingState === 'joined-meeting';

  const { data: appointmentsData, isPending: isLoadingUpcomingAppointments } =
    useGetUpcomingAppointments();

  const nextAppointment = useMemo(() => {
    if (
      !appointmentsData?.upcomingAppointments ||
      !appointmentsData.upcomingAppointments.length ||
      !appointment?.healthie?.id
    ) {
      return null;
    }
    const currentAppointmentIndex =
      appointmentsData.upcomingAppointments.findIndex(
        (a: UpcomingAppointment) => a.id === appointment?.healthie?.id,
      );

    const nextAppointment = appointmentsData.upcomingAppointments.find(
      (a: UpcomingAppointment, idx: number) => {
        if (idx <= currentAppointmentIndex) return false;

        const currentAppointmentDate = sanitizeHealthieDate(
          appointment?.healthie?.date,
        );
        const nextAppointmentDate = sanitizeHealthieDate(a.date);

        const isSameDay =
          format(new Date(), 'M/d/yyyy') ===
          format(nextAppointmentDate, 'M/d/yyyy');

        const isInFuture = currentAppointmentDate < nextAppointmentDate;
        return isSameDay && isInFuture;
      },
    );
    return nextAppointment;
  }, [appointment?.healthie, appointmentsData?.upcomingAppointments]);

  return (
    <>
      <HStack
        as="header"
        justifyContent={justifyContent || (user ? 'space-between' : 'center')}
        alignItems="center"
        w="100vw"
        h="56px"
        p="9px 24px"
        top="0"
        position="fixed"
        className={styles.headerHeight}
        background={user ? '#1B1F26' : 'transparent'}
      >
        <HStack gap={8}>
          <Flex justifyContent="center" alignItems="center">
            {logoConfig && (
              <Image
                src={logoConfig.getLogoSrc({
                  logoColor,
                  variant: isCallOn ? 'light' : 'default',
                })}
                alt={logoConfig.altText}
                width={!user ? 160 : 128}
                height={!user ? 40 : 32}
                style={{
                  objectFit: 'contain',
                  filter:
                    logoColor === 'white' || isCallOn
                      ? logoConfig.whiteLogoFilter
                      : undefined,
                }}
              />
            )}
          </Flex>

          {isProviderInCall && (
            <>
              {!!appointment?.healthie?.appointment_type && (
                <HeaderInfoBlock
                  label="Appointment type"
                  text={appointment?.healthie?.appointment_type?.name}
                />
              )}
              <PatientTags tags={getAppointmentTags(appointment)} />
              {!isLoadingUpcomingAppointments && (
                <HeaderInfoBlock
                  label="Next session"
                  text={
                    nextAppointment
                      ? format(
                          sanitizeHealthieDate(nextAppointment.date),
                          'h:mm a',
                        )
                      : "You're done for today!"
                  }
                />
              )}
            </>
          )}
        </HStack>

        {chartingNoteEnabled && (
          <Box
            position="absolute"
            top="30px"
            left="50%"
            transform="translate(-50%, -50%)"
          >
            {meetingState === 'joined-meeting' &&
              recordingState === RecordingState.Waiting && (
                <Tooltip
                  background="none"
                  label={
                    <Toast
                      withArrow
                      title="Recording waiting to start"
                      text="The recording with start automatically when the patient joins the session."
                    />
                  }
                >
                  <Box
                    fontSize="12px"
                    color="#ABABAB"
                    fontWeight="500"
                    display="flex"
                    gap="4px"
                    alignItems="center"
                  >
                    <IoIosMore size="22px" fill="#ABABAB" /> Waiting to start
                    charting
                  </Box>
                </Tooltip>
              )}

            {recordingState === RecordingState.Recording && <ChartingTag />}
          </Box>
        )}

        <HStack gap={8}>
          {isProviderInCall && present > 1 && (
            <>
              <HeaderInfoBlock
                label="Time passed"
                text="0"
                subText="min"
                text2="0"
                subText2="unit"
                textRef={totalTimeRef}
                subTextRef={subTextRef}
                text2Ref={totalUnitsRef}
                subText2Ref={subText2Ref}
                tooltip2="Billable units are 15-minute increments used for insurance reimbursement. Sessions lasting at least 8 minutes into the next unit round up, while those under 7 minutes round down."
              />

              <HeaderInfoBlock
                label="Next unit"
                text="15:00"
                textRef={nextUnitRef}
                tooltip="Time remaining until your next billable unit unlocks! We provide this so you can easily pace your appointments and maximize billing."
              />
            </>
          )}

          {rightPanel
            ? rightPanel
            : !withoutUserButton && user && <UserButton />}
        </HStack>
      </HStack>

      {/* compensation for the height of the header */}
      <Box h="7vh" data-info="header-compensator" />
    </>
  );
};

export default Header;
